import React, {
  FC,
  useState,
  useEffect,
  MouseEvent,
  ReactNode,
  useCallback,
  useRef,
} from "react";
import classNames from "classnames";
import { CSSTransition } from "react-transition-group";
import { ITouchEvent, View } from "@tarojs/components";
import { pxTransform } from "@/utils";

import Badge from "../Badge";
import "./index.scss";
import { XHComponentCommonProps } from "../../types";
import Space from "../Space";

export type NumberKeyboardProps = {
  show: boolean;
  /**
   * 是否支持小数，小数保留小数点后两位，默认不支持
   */
  decimal?: boolean;
  showClose?: boolean;
  closeOnClickSpace?: boolean;
  maxLength?: number;
  onBlur?: (e: MouseEvent | Event) => void;
  onClose?: (e: MouseEvent | Event) => void;
  onChange?: (value: string) => void;
  value: string;
  header?: ReactNode;
  /**
   *
   * @param value 输入值
   * @param recommend 是否是点击推荐金额触发
   * @returns void
   */
  onConfirm?: (value: string, recommend?: boolean) => void;
  recommend?: Array<number | string>;
  tip?: ReactNode;
  disabled?: boolean;
  dataTrackId?: string;
} & XHComponentCommonProps;

export type KeyType = "number" | "delete" | "string" | "none" | "close";

export interface IKey {
  text: number | string;
  value: number | string;
  type: KeyType;
}

const XHAmountKeys: IKey[] = [
  { text: 1, value: 1, type: "number" },
  { text: 2, value: 2, type: "number" },
  { text: 3, value: 3, type: "number" },
  { text: "", value: "", type: "delete" },
  { text: 4, value: 4, type: "number" },
  { text: 5, value: 5, type: "number" },
  { text: 6, value: 6, type: "number" },
  { text: "", value: "", type: "none" },
  { text: 7, value: 7, type: "number" },
  { text: 8, value: 8, type: "number" },
  { text: 9, value: 9, type: "number" },
  { text: "", value: "", type: "none" },
  { text: "00", value: "00", type: "number" },
  { text: 0, value: 0, type: "number" },
  { text: "", value: "", type: "close" },
  { text: "", value: "", type: "none" },
];

const XHAmountKeysWithDot: IKey[] = [
  { text: 1, value: 1, type: "number" },
  { text: 2, value: 2, type: "number" },
  { text: 3, value: 3, type: "number" },
  { text: "", value: "", type: "delete" },
  { text: 4, value: 4, type: "number" },
  { text: 5, value: 5, type: "number" },
  { text: 6, value: 6, type: "number" },
  { text: "", value: "", type: "none" },
  { text: 7, value: 7, type: "number" },
  { text: 8, value: 8, type: "number" },
  { text: 9, value: 9, type: "number" },
  { text: "", value: "", type: "none" },
  { text: "·", value: ".", type: "number" },
  { text: 0, value: 0, type: "number" },
  { text: "", value: "", type: "close" },
  { text: "", value: "", type: "none" },
];

const AmountKeyboard: FC<NumberKeyboardProps> = props => {
  const {
    onBlur,
    show,
    maxLength = Number.MAX_SAFE_INTEGER,
    closeOnClickSpace = true,
    onClose,
    onChange,
    value,
    className,
    onConfirm,
    recommend = [],
    header,
    disabled = false,
    tip,
    decimal,
    dataTrackId = "Amount_Keyboard",
  } = props;
  const [input, setInput] = useState(value);

  useEffect(() => {
    if (closeOnClickSpace) {
      document.addEventListener("click", handleClose);
    }

    return () => {
      document.removeEventListener("click", handleClose);
    };
  }, []);

  useEffect(() => {
    setInput(value);
  }, [value]);

  function handleClose(e): void {
    onClose && onClose(e);
    onBlur && onBlur(e);
  }

  const handleOnRecommand = (e: ITouchEvent, amount: number | string) => {
    e.stopPropagation();
    onConfirm?.(`${amount}`, true);
  };

  const handleKeyPress = useCallback(
    (e, { type, value }: IKey): void => {
      if (process.env.TARO_ENV === "h5") {
        e.stopImmediatePropagation();
      }
      if (disabled) return;
      if (type === "none") return;
      // 第一个字符不能是.
      if (value === "." && input === "") return;
      if (type === "close") {
        handleClose(e);
        return;
      }
      let newInput = input;
      if (type === "delete") {
        newInput = input.slice(0, input.length - 1);
      } else if (newInput.replace(/^0+/g, "").length < maxLength) {
        newInput = input + value;
      }
      // 支持小数点的情况下，如果第一个是0，自动填充.
      if (decimal && value === 0 && input === "") newInput = value + ".";

      // 不能包含多个小数点
      if (value === "." && input.includes(".")) return;
      // 保留两位小数
      if (
        type === "number" &&
        input.indexOf(".") > -1 &&
        input.length - input.indexOf(".") === 3
      )
        return;

      // 前后两个值相等就不触发onchange
      if (newInput === input) return;
      setInput(newInput);
      onChange?.(newInput);
    },
    [input, disabled]
  );

  const renderKeys = decimal ? XHAmountKeysWithDot : XHAmountKeys;

  const handleConfirm = (e: ITouchEvent) => {
    e.stopPropagation();
    onConfirm?.(input);
  };
  const keyboardcls = classNames("xh-amount-keyboard", className);

  const nodeRef = useRef(null);

  return (
    <CSSTransition
      nodeRef={nodeRef}
      classNames="slide-up"
      in={show}
      unmountOnExit
      timeout={200}
    >
      <View className={keyboardcls}>
        <View className="xh-amount-keyboard-header">
          <View className="xh-amount-keyboard-header-wrapper">
            {header ||
              (recommend.length > 0 && (
                <View className="xh-amount-keyboard-header-wrapper-text">
                  猜您想借{" "}
                  <Space style={{ marginLeft: pxTransform(24) }}>
                    {recommend?.map((amount, index) => (
                      <View
                        // @ts-ignore
                        dataTrackId={`${dataTrackId}-recommend-${index}`}
                        key={index}
                        onClick={e => handleOnRecommand(e, amount)}
                      >
                        <Badge content={`¥${amount}`} key={index} />
                      </View>
                    ))}
                  </Space>
                </View>
              ))}
            {tip}
          </View>
        </View>
        <View className="xh-amount-keyboard-body">
          {renderKeys.map((key, index) => (
            <View
              key={index}
              // @ts-ignore
              dataTrackId={`${dataTrackId}-key-${index}`}
              className={classNames("xh-amount-keyboard-body-key", {
                none: key.type === "none",
                delete: key.type === "delete",
                close: key.type === "close",
              })}
              onClick={e => {
                handleKeyPress(e, key);
              }}
            >
              {key.text}
            </View>
          ))}
          <View
            // @ts-ignore
            dataTrackId={`${dataTrackId}-confirm-fake`}
            className="xh-amount-keyboard-body-key confirm-fake"
            onClick={handleConfirm}
          >
            确定
          </View>
        </View>
      </View>
    </CSSTransition>
  );
};

export default AmountKeyboard;
